import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Col,
  ListGroup,
  Offcanvas,
  Row
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { shipStationOrderUrl } from '../config/rateShopConstants';
import { money, primaryOrderId } from '../lib/describe';
import StoreLabel from '../components/StoreLabel';
import { sameRate } from '../lib/previewRule';
import {
  ConfirmModal,
  dateTime,
  errorText,
  LoadError,
  Loading,
  RateLabel,
  StatusBadge
} from '../components/Common';
import RatesTable from '../components/RatesTable';

const JsonBlock = ({ value }) => (
  <pre
    className="bg-light border rounded p-2 small text-break"
    style={{ whiteSpace: 'pre-wrap' }}
  >
    {JSON.stringify(value, null, 2)}
  </pre>
);

const DecisionDrawer = ({
  id,
  getDecision,
  deleteDecision,
  canEdit,
  onHide,
  stores
}) => {
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getDecision(id)
      .then(found => {
        if (!active) return;
        if (!found)
          throw new Error(
            'This decision no longer exists. Refresh the log to see the latest decisions.'
          );
        setDecision(found);
      })
      .catch(err => {
        if (active) setError(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, getDecision]);

  const rerate = async () => {
    if (!canEdit || busy) return;
    setBusy(true);
    try {
      await deleteDecision(id);
      toast.success(
        'Decision cleared. This shipment will be eligible for the next sweep.'
      );
      setBusy(false);
      onHide();
    } catch (err) {
      toast.error(err.message);
      setBusy(false);
    }
  };
  const context = decision?.context || {};
  const externalOrderId = decision?.externalOrderId || context.externalOrderId;
  const orderId = primaryOrderId(decision) || id;
  const candidates = decision?.candidates || [];
  const otherRates = (decision?.rates || []).filter(
    rate => !candidates.some(c => sameRate(c, rate))
  );

  return (
    <>
      <Offcanvas
        show
        placement="end"
        onHide={busy ? undefined : onHide}
        style={{ width: 'min(1100px, 95vw)' }}
      >
        <Offcanvas.Header closeButton={!busy}>
          <Offcanvas.Title>Decision · {orderId}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {loading ? (
            <Loading label="Loading fresh decision…" />
          ) : (
            <LoadError error={error} />
          )}
          {!loading && decision && (
            <>
              <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                <StatusBadge status={decision.status} />
                <span className="small text-muted">
                  {dateTime(decision.decidedAt)} · {id}
                </span>
              </div>
              {decision.reason && (
                <Alert variant="info">{decision.reason}</Alert>
              )}
              <div className="d-flex flex-wrap gap-2 mb-4">
                {canEdit && (
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={busy}
                    onClick={() => setConfirm(true)}
                  >
                    Re-rate on next run
                  </Button>
                )}
                {orderId && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    as="a"
                    href={shipStationOrderUrl(orderId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in ShipStation{' '}
                    <FontAwesomeIcon
                      icon={faExternalLinkAlt}
                      className="ms-1"
                    />
                  </Button>
                )}
              </div>
              <h5>Shipment context</h5>
              <Row className="g-3 small mb-3">
                <Col sm={6}>
                  <strong>Order # (PO):</strong> {orderId}
                  <br />
                  <strong>Marketplace order id:</strong>{' '}
                  {externalOrderId || '—'}
                  <br />
                  <strong>Shipment id:</strong> {decision.id || id}
                  <br />
                  <strong>Store:</strong>{' '}
                  <StoreLabel
                    id={decision.storeId || context.storeId}
                    stores={stores}
                  />
                  <br />
                  <strong>Destination:</strong>{' '}
                  {[context.state, context.postalCode, context.countryCode]
                    .filter(Boolean)
                    .join(', ') || '—'}
                  <br />
                  <strong>Residential:</strong>{' '}
                  {context.residential == null
                    ? '—'
                    : context.residential
                    ? 'Yes'
                    : 'No'}
                </Col>
                <Col sm={6}>
                  <strong>Weight:</strong> {context.weightOz ?? '—'} oz
                  <br />
                  <strong>Dimensions:</strong>{' '}
                  {typeof context.dims === 'string'
                    ? context.dims
                    : context.dims
                    ? JSON.stringify(context.dims)
                    : '—'}
                  <br />
                  <strong>Items:</strong> {context.itemCount ?? '—'} ·{' '}
                  <strong>Order total:</strong> {money(context.orderTotal)}
                  <br />
                  <strong>Has dimensions:</strong>{' '}
                  {context.hasDims ? 'Yes' : 'No'}
                </Col>
                <Col xs={12}>
                  <strong>Tags:</strong>{' '}
                  {(context.tags || []).join(', ') || '—'}
                  <br />
                  <strong>SKUs:</strong>{' '}
                  {(context.skus || []).join(', ') || '—'}
                  <br />
                  <strong>Assigned:</strong>{' '}
                  {[
                    context.currentCarrierId,
                    context.currentServiceCode,
                    context.currentPackageCode
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </Col>
              </Row>
              <p className="small mb-1">
                <strong>Winner:</strong> <RateLabel rate={decision.winner} />
              </p>
              <p className="small mb-1">
                <strong>Previous rate:</strong>{' '}
                <RateLabel rate={decision.currentRate} />
              </p>
              <p className="small mb-4">
                <strong>Savings vs current:</strong>{' '}
                {money(decision.savings?.vsCurrent)} ·{' '}
                <strong>vs next best:</strong>{' '}
                {money(decision.savings?.vsNextBest)}
                <br />
                <strong>Run:</strong> {decision.runId || '—'}
              </p>
              <h5>Rule trace</h5>
              <ol className="list-unstyled border-start ps-3 mb-4">
                {(decision.rulesTrace || []).map((trace, i) => (
                  <li
                    key={i}
                    className={`border-start border-3 ps-3 pb-3 ${
                      trace.matched ? 'border-success' : 'border-secondary'
                    }`}
                  >
                    <div className="small">
                      <Badge
                        bg={trace.matched ? 'success' : 'secondary'}
                        className="me-2"
                      >
                        {trace.reason === 'stopProcessing'
                          ? 'Stopped here'
                          : trace.matched
                          ? 'Matched'
                          : 'Not matched'}
                      </Badge>
                      <strong>{trace.name || trace.ruleId}</strong>
                      {trace.priority != null && (
                        <span className="text-muted">
                          {' '}
                          · priority {trace.priority}
                        </span>
                      )}
                    </div>
                    <div className="small text-muted">{trace.ruleId}</div>
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {(trace.applied || []).map((action, j) => (
                        <Badge key={j} bg="info">
                          {action}
                        </Badge>
                      ))}
                    </div>
                    {trace.reason && (
                      <p className="small mb-0 mt-1">
                        {trace.reason === 'stopProcessing'
                          ? 'Later rules were not evaluated.'
                          : trace.reason}
                      </p>
                    )}
                  </li>
                ))}
                {!decision.rulesTrace?.length && (
                  <li className="text-muted small">No rule trace recorded.</li>
                )}
              </ol>
              <h5>Candidates ({candidates.length})</h5>
              <RatesTable rates={candidates} winner={decision.winner} />
              <details className="mb-4">
                <summary className="text-primary mb-2">
                  All rates seen ({decision.rates?.length || 0}) ·{' '}
                  {otherRates.length} outside candidate list
                </summary>
                <h6>Additional rates outside the candidate list</h6>
                <RatesTable rates={otherRates} winner={decision.winner} />
              </details>
              <h5>Carrier groups</h5>
              <ListGroup className="mb-4">
                {Object.entries(decision.groups || {}).map(
                  ([group, result]) => (
                    <ListGroup.Item key={group} className="small">
                      <strong>{group}</strong> ·{' '}
                      {result.ran ? 'Ran' : 'Did not run'} ·{' '}
                      {result.status || '—'} · {result.count ?? 0} rates
                      {(result.errors || []).map((message, i) => (
                        <Alert
                          key={i}
                          variant="danger"
                          className="py-2 mt-2 mb-0 text-break"
                        >
                          {errorText(message)}
                        </Alert>
                      ))}
                    </ListGroup.Item>
                  )
                )}
                {!Object.keys(decision.groups || {}).length && (
                  <ListGroup.Item className="text-muted small">
                    No group results recorded.
                  </ListGroup.Item>
                )}
              </ListGroup>
              {decision.error && (
                <>
                  <h5>Error</h5>
                  <Alert variant="danger" className="text-break">
                    {errorText(decision.error)}
                  </Alert>
                </>
              )}
              <h5>Applied payload</h5>
              {decision.applied ? (
                <JsonBlock value={decision.applied} />
              ) : (
                <p className="text-muted small">No write-back recorded.</p>
              )}
              {decision.plan && (
                <details className="mb-4">
                  <summary className="text-primary mb-2">
                    Accumulated rule plan
                  </summary>
                  <JsonBlock value={decision.plan} />
                </details>
              )}
              <h5>Previous outcomes</h5>
              <ListGroup className="mb-3">
                {(decision.history || []).map((entry, i) => (
                  <ListGroup.Item key={i} className="small">
                    <div className="d-flex gap-2 mb-1">
                      <StatusBadge status={entry.status} />
                      <span>{dateTime(entry.decidedAt)}</span>
                    </div>
                    <RateLabel rate={entry.winner} />
                    <div className="text-muted">Run: {entry.runId || '—'}</div>
                  </ListGroup.Item>
                ))}
                {!decision.history?.length && (
                  <ListGroup.Item className="text-muted small">
                    No prior outcomes.
                  </ListGroup.Item>
                )}
              </ListGroup>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
      <ConfirmModal
        show={confirm}
        title="Re-rate on next run?"
        busy={busy}
        onHide={() => setConfirm(false)}
        onConfirm={rerate}
        confirmLabel={
          ['applied', 'human-override', 'reverted'].includes(decision?.status)
            ? 'Clear protection and re-rate'
            : 'Clear decision'
        }
      >
        <p>
          This deletes the stored decision and its history for {id}. The next
          sweep can evaluate the shipment again if it remains in scope.
        </p>
        {['applied', 'human-override', 'reverted'].includes(decision?.status) && (
          <Alert variant="warning">
            The current service is{' '}
            {(decision.status === 'applied'
              ? decision.applied?.serviceCode ||
                decision.winner?.serviceCode ||
                context.currentServiceCode
              : context.currentServiceCode ||
                decision.applied?.serviceCode ||
                decision.winner?.serviceCode) || 'not recorded'}
            . Clearing this decision removes its written/override protection,
            and the next live run may overwrite that service.
          </Alert>
        )}
        <p className="mb-0">
          When dry run is off, that sweep may write a new service to
          ShipStation.
        </p>
      </ConfirmModal>
    </>
  );
};

export default DecisionDrawer;
